import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ActiveVenueContext,
  VenueApiResponse,
  VenueFeature,
  VenueSummary,
} from '../models/venue-context.models';
import { TokenStorageService } from './token-storage.service';

@Injectable({ providedIn: 'root' })
export class VenueContextService {
  private readonly http = inject(HttpClient);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly endpoint = `${environment.apiUrl}venue-context`;

  private readonly venuesSubject = new BehaviorSubject<readonly VenueSummary[]>([]);
  private readonly activeVenueSubject =
    new BehaviorSubject<ActiveVenueContext | null>(null);

  readonly venues$ = this.venuesSubject.asObservable();
  readonly activeVenue$ = this.activeVenueSubject.asObservable();
  readonly enabledFeatures$ = this.activeVenue$.pipe(
    map((venue) => new Set((venue?.enabledFeatures ?? []).map((item) => item.featureCode))),
  );

  constructor() {
    this.hydratePersistedContext();
  }

  get venues(): readonly VenueSummary[] {
    return this.venuesSubject.value;
  }

  get activeVenue(): ActiveVenueContext | null {
    return this.activeVenueSubject.value;
  }

  initialize(): Observable<ActiveVenueContext | null> {
    const user = this.tokenStorage.getUser();
    if (!user) {
      this.clear();
      return of(null);
    }

    return this.loadAccessibleVenues().pipe(
      switchMap((venues) => {
        if (venues.length === 0) {
          this.tokenStorage.removeActiveVenueId();
          this.tokenStorage.removeActiveVenueContext();
          this.activeVenueSubject.next(null);
          return of(null);
        }

        const storedBusinessId = this.tokenStorage.getActiveVenueId();
        const preferredBusinessId = storedBusinessId ?? user.businessId ?? 0;
        const selectedVenue =
          venues.find((venue) => venue.businessId === preferredBusinessId) ??
          venues[0];

        return this.selectVenue(selectedVenue.businessId);
      }),
      catchError((error) => {
        // Compatibility fallback while the database migration and API are rolled out.
        console.error('Unable to initialize validated venue context.', error);
        const legacyBusinessId = this.tokenStorage.getActiveVenueId() ?? user.businessId;
        if (!legacyBusinessId) {
          this.clear();
          return of(null);
        }

        const fallback: ActiveVenueContext = {
          businessId: legacyBusinessId,
          organizationId: user.organizationId ?? 0,
          organizationName: '',
          venueName: '',
          logoUrl: user.BusinessProfileImageUrl ?? '',
          venueRoleId: user.roleId ?? 0,
          venueRoleName: user.roleName ?? '',
          isOrganizationAdmin:
            user.roleId === 4 || user.roles.includes('OrgAdmin'),
          enabledFeatures: this.defaultFeatures(),
        };
        this.tokenStorage.setActiveVenueId(legacyBusinessId);
        this.activeVenueSubject.next(fallback);
        return of(fallback);
      }),
    );
  }

  loadAccessibleVenues(): Observable<readonly VenueSummary[]> {
    return this.http
      .get<VenueApiResponse<VenueSummary[]>>(`${this.endpoint}/venues`)
      .pipe(
        map((response) =>
          response.success
            ? (response.data ?? []).map((venue) => ({
                ...venue,
                logoUrl: this.resolveBusinessLogoUrl(venue.logoUrl),
              }))
            : [],
        ),
        tap((venues) => this.venuesSubject.next(venues)),
      );
  }

  selectVenue(businessId: number): Observable<ActiveVenueContext> {
    if (!Number.isInteger(businessId) || businessId <= 0) {
      throw new Error('A positive venue identifier is required.');
    }

    return this.http
      .post<VenueApiResponse<ActiveVenueContext>>(`${this.endpoint}/active`, {
        businessId,
      })
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            throw new Error(response.message || 'Venue access was denied.');
          }
          return {
            ...response.data,
            logoUrl: this.resolveBusinessLogoUrl(response.data.logoUrl),
          };
        }),
        tap((activeVenue) => {
          this.tokenStorage.setActiveVenueId(activeVenue.businessId);
          this.tokenStorage.setActiveVenueContext(activeVenue);
          const currentUser = this.tokenStorage.getUser();
          if (currentUser) {
            this.tokenStorage.setUser({
              ...currentUser,
              businessId: activeVenue.businessId,
              organizationId: activeVenue.organizationId,
              roleId: activeVenue.venueRoleId,
              roleName: activeVenue.venueRoleName,
              BusinessProfileImageUrl: activeVenue.logoUrl || currentUser.BusinessProfileImageUrl,
            });
          }
          this.activeVenueSubject.next(activeVenue);
        }),
      );
  }

  hasFeature(featureCode: string): boolean {
    return (this.activeVenue?.enabledFeatures ?? []).some(
      (feature) => feature.featureCode === featureCode,
    );
  }

  clear(): void {
    this.venuesSubject.next([]);
    this.activeVenueSubject.next(null);
    this.tokenStorage.removeActiveVenueId();
    this.tokenStorage.removeActiveVenueContext();
  }

  private hydratePersistedContext(): void {
    const persistedContext = this.tokenStorage.getActiveVenueContext();
    if (persistedContext) {
      this.activeVenueSubject.next({
        ...persistedContext,
        logoUrl: this.resolveBusinessLogoUrl(persistedContext.logoUrl),
      });
      return;
    }

    const user = this.tokenStorage.getUser();
    const businessId = this.tokenStorage.getActiveVenueId() ?? user?.businessId;
    if (!user || !businessId) return;

    this.activeVenueSubject.next({
      businessId,
      organizationId: user.organizationId ?? 0,
      organizationName: '',
      venueName: '',
      logoUrl: user.BusinessProfileImageUrl ?? '',
      venueRoleId: user.roleId ?? 0,
      venueRoleName: user.roleName ?? '',
      isOrganizationAdmin:
        user.roleId === 4 || user.roles.includes('OrgAdmin'),
      enabledFeatures: this.defaultFeatures(),
    });
  }

  private defaultFeatures(): VenueFeature[] {
    return [
      'dashboard',
      'showcases',
      'chat',
      'reports',
      'payments',
      'team-management',
      'subscription',
    ].map((featureCode) => ({ featureCode }));
  }

  private resolveBusinessLogoUrl(logoUrl: string | null | undefined): string {
    const value = logoUrl?.trim() ?? '';
    if (!value || value.toLowerCase() === 'avatar.png') {
      return '';
    }

    if (/^(https?:|data:|blob:)/i.test(value)) {
      return value;
    }

    const profileLogoBaseUrl = environment.blobVenueImgUrl.replace(
      /venue-images\/?$/i,
      'profile-logo/',
    );
    return `${profileLogoBaseUrl}${encodeURIComponent(value)}`;
  }
}
